/**
 * Zugriff auf die Schnittstelle.
 *
 * Der Server antwortet bei Fehlern mit `{ error, message, details }`. Die
 * Meldung ist bereits deutsch formuliert und fuer den Anwender gedacht -
 * sie wird unveraendert angezeigt. `details` enthaelt bei Formularfehlern
 * die Zuordnung Feld -> Meldung.
 */

export type FieldErrors = Record<string, string>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: FieldErrors;

  constructor(status: number, code: string, message: string, fieldErrors: FieldErrors = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }

  /** Ist der Benutzer abgemeldet oder die Sitzung abgelaufen? */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}


/**
 * Basispfad der Oberflaeche ("" oder z. B. "/wissen"), von Vite beim Bauen
 * gesetzt (*_BASE_PATH). Jede Adresse an den Server geht durch apiUrl, damit
 * die Anwendung auch unter einem Unterpfad hinter einem Proxy laeuft.
 */
const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, '');

export function apiUrl(path: string): string {
  return path.startsWith('/') ? `${BASE_PATH}${path}` : path;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;

  try {
    response = await fetch(apiUrl(path), {
      method,
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(
      0,
      'NETWORK',
      'Der Server ist nicht erreichbar. Laeuft er noch, und besteht eine Netzwerkverbindung?',
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const data = (payload ?? {}) as { error?: string; message?: string; details?: FieldErrors };
    throw new ApiError(
      response.status,
      data.error ?? 'UNKNOWN',
      data.message ?? `Die Anfrage ist fehlgeschlagen (Status ${response.status}).`,
      data.details ?? {},
    );
  }

  return payload as T;
}

/** Haengt Abfrageparameter an, leere Werte werden weggelassen. */
export function withQuery(path: string, params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
